import { Link, Outlet, useLocation } from 'react-router';
import { BookOpen, Plug, FileCode2 } from 'lucide-react';
import { StickyNav } from '@/components/landing/StickyNav';

const academyTabs = [
  { to: '/docs/guide', label: 'Web App Guide', icon: BookOpen },
  { to: '/docs/mcp', label: 'MCP Integration', icon: Plug },
  { to: '/docs/api-reference', label: 'API Reference', icon: FileCode2 },
];

/**
 * Public layout for academy pages — accessible without login.
 * Renders the landing-page StickyNav, an academy tab bar, the page content,
 * and a minimal footer. The <Outlet /> renders the same academy page components
 * used inside the authenticated app.
 */
export function PublicAcademyLayout() {
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen bg-base-100 flex flex-col">
      {/* Landing-page nav */}
      <StickyNav />

      {/* Academy tab bar */}
      <div className="border-b border-base-300/30 bg-base-100/80 backdrop-blur-sm sticky top-[68px] z-40">
        <div className="max-w-5xl mx-auto px-6 flex gap-1 overflow-x-auto">
          {academyTabs.map((tab) => (
            <Link
              key={tab.to}
              to={tab.to}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                pathname === tab.to
                  ? 'border-primary text-primary'
                  : 'border-transparent text-base-content/50 hover:text-base-content'
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
            </Link>
          ))}
        </div>
      </div>

      {/* Page content */}
      <main className="flex-1 max-w-5xl mx-auto w-full px-6 py-10">
        <Outlet />
      </main>

      {/* Minimal footer */}
      <footer className="border-t border-base-300/30 py-8 px-6">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-4 text-xs text-base-content/30">
          <Link to="/" className="text-sm font-extrabold tracking-tight text-base-content/50 hover:text-base-content transition-colors">
            Hy<span className="text-primary">Pe</span>Ma
          </Link>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-base-content/50 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-base-content/50 transition-colors">Terms</Link>
            <Link to="/imprint" className="hover:text-base-content/50 transition-colors">Imprint</Link>
            <a href="/llms.txt" className="hover:text-base-content/50 transition-colors">llms.txt</a>
          </div>
          <span>&copy; {new Date().getFullYear()} HyPeMa</span>
        </div>
      </footer>
    </div>
  );
}
